import { FormEvent, ReactNode, useEffect, useState } from "react";
import { api } from "./api";
import { BrcPrimaryLogo, ErrorNotice, JourneyProgress, LoadingScreen, PageShell, PathwayMark, Progress } from "./components";
import { Summary } from "./Summary";
import type {
  InventoryContent,
  JourneyAnswer,
  Letter,
  Participant,
  ParticipantSession,
} from "./types";
import cityArtwork from "../../ReqsAndAssets/1 - Brand Assets/Logos/Transparent City Only.png";
import linktreeQr from "../../ReqsAndAssets/1 - Brand Assets/Linktree QR.png";

const SESSION_KEY = "redemptive-builder-inventory-session";
type Screen =
  | "welcome"
  | "resume"
  | "info"
  | "part-1"
  | "inventory"
  | "part-2"
  | "part-3"
  | "reveal"
  | "journey-intro"
  | "journey"
  | "summary"
  | "complete";

export function ParticipantApp({ resultToken, resumeToken }: { resultToken?: string; resumeToken?: string }) {
  const [content, setContent] = useState<InventoryContent | null>(null);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [accessToken, setAccessToken] = useState("");
  const [screen, setScreen] = useState<Screen>(resultToken ? "summary" : resumeToken ? "resume" : "welcome");
  const [inventoryIndex, setInventoryIndex] = useState(0);
  const [journeyIndex, setJourneyIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const loadedContent = await api.content();
        if (!active) return;
        setContent(loadedContent);
        if (resultToken) {
          const result = await api.result(resultToken);
          if (active) setParticipant(result);
          return;
        }
        if (resumeToken) return;
        const saved = readSession();
        if (saved) {
          try {
            const resumed = await api.participant(saved.id, saved.token);
            if (!active) return;
            setParticipant(resumed);
            setAccessToken(saved.token);
            restorePosition(resumed, setScreen, setInventoryIndex, setJourneyIndex);
          } catch {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      } catch (caught) {
        if (active) setError(messageOf(caught));
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [resultToken, resumeToken]);

  async function moveTo(next: Screen, currentStep = 0) {
    setError("");
    if (participant && accessToken) {
      try {
        const updated = await api.stage(participant.id, accessToken, next, currentStep);
        setParticipant(updated);
      } catch (caught) {
        setError(messageOf(caught));
        return;
      }
    }
    setScreen(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (loading) return <LoadingScreen />;
  if (!content) {
    return <PageShell><div className="center-card"><ErrorNotice message={error || "The Inventory content could not be loaded."} /></div></PageShell>;
  }
  if (resultToken && !participant) {
    return <PageShell><div className="center-card"><ErrorNotice message={error || "That personalized summary is unavailable."} /></div></PageShell>;
  }

  const journeyPage = (page: ReactNode) => resultToken ? page : <>{page}<JourneyProgress screen={screen} /></>;

  if (screen === "resume" && resumeToken) {
    return journeyPage(
      <ResumeScreen
        resumeToken={resumeToken}
        onResumed={(session) => {
          localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ id: session.participant.id, token: session.accessToken }),
          );
          setParticipant(session.participant);
          setAccessToken(session.accessToken);
          restorePosition(session.participant, setScreen, setInventoryIndex, setJourneyIndex);
          window.history.replaceState({}, "", "/");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />,
    );
  }

  if (screen === "welcome") {
    return journeyPage(<Welcome content={content} onBegin={() => setScreen("info")} />);
  }
  if (screen === "info") {
    return journeyPage(
      <ParticipantInfo
        busy={busy}
        error={error}
        onBack={() => setScreen("welcome")}
        onSubmit={async (values) => {
          setBusy(true);
          setError("");
          try {
            const source = sourceFromLocation();
            const session = await api.start({ ...values, source });
            localStorage.setItem(
              SESSION_KEY,
              JSON.stringify({ id: session.participant.id, token: session.accessToken }),
            );
            setParticipant(session.participant);
            setAccessToken(session.accessToken);
            setScreen("part-1");
          } catch (caught) {
            setError(messageOf(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }
  if (!participant) return journeyPage(<LoadingScreen />);

  if (screen.startsWith("part-")) {
    const partNumber = Number(screen.slice(-1));
    return journeyPage(
      <PartTransition
        content={content}
        partNumber={partNumber}
        onBack={() => {
          if (partNumber === 1) setScreen("info");
          else {
            const previous = partNumber === 2 ? 4 : 9;
            setInventoryIndex(previous);
            void moveTo("inventory", previous);
          }
        }}
        onContinue={() => {
          const index = partNumber === 1 ? 0 : partNumber === 2 ? 5 : 10;
          setInventoryIndex(index);
          void moveTo("inventory", index);
        }}
      />
    );
  }
  if (screen === "inventory") {
    return journeyPage(
      <InventoryQuestionScreen
        content={content}
        participant={participant}
        index={inventoryIndex}
        busy={busy}
        error={error}
        onBack={() => {
          if ([0, 5, 10].includes(inventoryIndex)) {
            void moveTo(`part-${inventoryIndex === 0 ? 1 : inventoryIndex === 5 ? 2 : 3}` as Screen);
          } else {
            setInventoryIndex((value) => value - 1);
            void moveTo("inventory", inventoryIndex - 1);
          }
        }}
        onNext={async (letter) => {
          setBusy(true);
          setError("");
          const question = content.questions[inventoryIndex];
          try {
            const updated = await api.inventory(
              participant.id,
              accessToken,
              question.number,
              letter,
            );
            setParticipant(updated);
            if (inventoryIndex === 4) await moveTo("part-2");
            else if (inventoryIndex === 9) await moveTo("part-3");
            else if (inventoryIndex === 11) await moveTo("reveal");
            else {
              setInventoryIndex((value) => value + 1);
              await moveTo("inventory", inventoryIndex + 1);
            }
          } catch (caught) {
            setError(messageOf(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }
  if (screen === "reveal") {
    return journeyPage(
      <PathwayReveal
        content={content}
        participant={participant}
        onContinue={() => void moveTo("journey-intro")}
      />
    );
  }
  if (screen === "journey-intro") {
    return journeyPage(
      <JourneyIntro
        participant={participant}
        onBack={() => void moveTo("reveal")}
        onContinue={() => {
          setJourneyIndex(0);
          void moveTo("journey", 0);
        }}
      />
    );
  }
  if (screen === "journey") {
    return journeyPage(
      <JourneyQuestionScreen
        content={content}
        participant={participant}
        index={journeyIndex}
        busy={busy}
        error={error}
        onBack={() => {
          if (journeyIndex === 0) void moveTo("journey-intro");
          else {
            setJourneyIndex((value) => value - 1);
            void moveTo("journey", journeyIndex - 1);
          }
        }}
        onNext={async (answer) => {
          setBusy(true);
          setError("");
          const question = content.journeyQuestions[journeyIndex];
          try {
            const updated = await api.journey(
              participant.id,
              accessToken,
              question.number,
              answer,
            );
            setParticipant(updated);
            if (journeyIndex === content.journeyQuestions.length - 1) {
              const response = await api.complete(participant.id, accessToken);
              setParticipant(response.participant);
              setScreen("summary");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
            else {
              setJourneyIndex((value) => value + 1);
              await moveTo("journey", journeyIndex + 1);
            }
          } catch (caught) {
            setError(messageOf(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }
  if (screen === "summary") {
    return journeyPage(
      <PageShell saved={!resultToken} className="summary-shell">
        <Summary
          content={content}
          participant={participant}
          onParticipantChange={setParticipant}
          onContinue={resultToken ? undefined : () => {
            localStorage.removeItem(SESSION_KEY);
            setScreen("complete");
          }}
        />
      </PageShell>
    );
  }
  return journeyPage(
    <CompletionScreen
      participant={participant}
      onSummary={() => setScreen("summary")}
    />
  );
}

function ResumeScreen({ resumeToken, onResumed }: { resumeToken: string; onResumed: (session: ParticipantSession) => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      onResumed(await api.resume(resumeToken, email));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <PageShell>
      <section className="form-card resume-card">
        <p className="eyebrow">Continue your Inventory</p>
        <h1>Welcome back.</h1>
        <p>Enter the same email address you used when you began. We'll securely restore your saved answers and take you to the exact place you left off.</p>
        <form onSubmit={submit}>
          <label>Email address <span>Required</span><input required type="email" maxLength={254} autoComplete="email" autoFocus value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <ErrorNotice message={error} />
          <div className="form-actions"><a className="button text" href="/">Start a new Inventory</a><button className="button" disabled={busy}>{busy ? "Finding your progress..." : "Continue where I left off →"}</button></div>
        </form>
      </section>
    </PageShell>
  );
}

function Welcome({ content, onBegin }: { content: InventoryContent; onBegin: () => void }) {
  return (
    <PageShell className="welcome-shell">
      <section className="hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">From inspiration to participation</p>
          <h1>Discover how God may have uniquely designed you to participate.</h1>
          <p className="hero-lede">The Inventory identifies a pattern. The Builder Journey begins helping you interpret what that pattern could mean in your actual life.</p>
          <button className="button" onClick={onBegin}>Begin the Inventory <span>→</span></button>
          <p className="time-note">Set aside about 15-20 thoughtful minutes. Your progress will save as you go.</p>
        </div>
        <div className="hero-art" aria-hidden="true">
          <span className="sun-disc" />
          <div className="hero-logo">
            <BrcPrimaryLogo />
          </div>
        </div>
      </section>
      <section className="welcome-content">
        <div>
          <p className="eyebrow">{content.subtitle}</p>
          <h2>There are no better pathways.</h2>
        </div>
        <div className="welcome-paragraphs">
          {content.welcome.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </section>
      <section className="journey-preview">
        {[
          ["01", "Inventory", "Choose the response that most sounds like you."],
          ["02", "Pathway discovery", "See the pattern reflected in your responses."],
          ["03", "Builder Journey", "Reflect on the places and people already in your life."],
          ["04", "Personal summary", "Keep your pathway and reflections together."],
        ].map(([number, title, copy]) => (
          <div key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p></div>
        ))}
      </section>
    </PageShell>
  );
}

type ParticipantInfoValues = {
  firstName: string;
  lastName: string;
  email: string;
  participantType: string;
  organization: string;
  profession: string;
  townOfResidence: string;
  countyOfResidence: string;
  stateOfResidence: string;
  churchYouAttend: string;
  privacyAcknowledged: boolean;
};

function ParticipantInfo({
  busy,
  error,
  onBack,
  onSubmit,
}: {
  busy: boolean;
  error: string;
  onBack: () => void;
  onSubmit: (values: ParticipantInfoValues) => Promise<void>;
}) {
  const [values, setValues] = useState<ParticipantInfoValues>({
    firstName: "",
    lastName: "",
    email: "",
    participantType: "",
    organization: "",
    profession: "",
    townOfResidence: "",
    countyOfResidence: "",
    stateOfResidence: "",
    churchYouAttend: "",
    privacyAcknowledged: false,
  });
  return (
    <PageShell>
      <section className="form-card participant-info-card">
        <p className="eyebrow">Before we begin</p>
        <h1>Tell us a little about you.</h1>
        <p>Your information keeps every part of the journey together and gives us the address where your personalized summary will be sent.</p>
        <form onSubmit={(event) => { event.preventDefault(); void onSubmit(values); }}>
          <div className="field-grid two">
            <label>First name <span>Required</span><input required maxLength={100} autoComplete="given-name" value={values.firstName} onChange={(e) => setValues({ ...values, firstName: e.target.value })} /></label>
            <label>Last name <span>Required</span><input required maxLength={100} autoComplete="family-name" value={values.lastName} onChange={(e) => setValues({ ...values, lastName: e.target.value })} /></label>
          </div>
          <label>Email address <span>Required</span><input required type="email" maxLength={254} autoComplete="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} /></label>
          <fieldset className="choice-fieldset">
            <legend>I am a <span>Required</span></legend>
            <div className="segmented-control">
              {["Student", "Professional"].map((choice) => (
                <label key={choice}><input required type="radio" name="participant-type" value={choice} checked={values.participantType === choice} onChange={() => setValues({ ...values, participantType: choice })} /><span>{choice}</span></label>
              ))}
            </div>
          </fieldset>
          <label>Company or school <span>Required</span><input required maxLength={200} value={values.organization} onChange={(e) => setValues({ ...values, organization: e.target.value })} /></label>
          <label>Profession or field of study <span>Optional</span><input maxLength={200} value={values.profession} onChange={(e) => setValues({ ...values, profession: e.target.value })} /></label>
          <div className="field-grid two">
            <label>Town of residence <span>Required</span><input required maxLength={150} autoComplete="address-level2" value={values.townOfResidence} onChange={(e) => setValues({ ...values, townOfResidence: e.target.value })} /></label>
            <label>County of residence <span>Required</span><input required maxLength={150} value={values.countyOfResidence} onChange={(e) => setValues({ ...values, countyOfResidence: e.target.value })} /></label>
          </div>
          <div className="field-grid two">
            <label>State of residence <span>Required</span><input required maxLength={100} autoComplete="address-level1" value={values.stateOfResidence} onChange={(e) => setValues({ ...values, stateOfResidence: e.target.value })} /></label>
            <label>Church you attend <span>Optional</span><input maxLength={200} value={values.churchYouAttend} onChange={(e) => setValues({ ...values, churchYouAttend: e.target.value })} /></label>
          </div>
          <label className="privacy-check"><input required type="checkbox" checked={values.privacyAcknowledged} onChange={(e) => setValues({ ...values, privacyAcknowledged: e.target.checked })} /><span>I understand my responses will be stored as one participant record and reviewed by the Building Redemptive Community team to support the Building Redemptive Community journey.</span></label>
          <ErrorNotice message={error} />
          <div className="form-actions"><button className="button text" type="button" onClick={onBack}>Back</button><button className="button" disabled={busy}>{busy ? "Saving..." : "Continue →"}</button></div>
        </form>
      </section>
    </PageShell>
  );
}

function PartTransition({ content, partNumber, onBack, onContinue }: { content: InventoryContent; partNumber: number; onBack: () => void; onContinue: () => void }) {
  const part = content.parts.find((item) => item.number === partNumber)!;
  return (
    <PageShell saved>
      <section className="transition-card">
        <span className="oversize-number">0{part.number}</span>
        <p className="eyebrow">Part {part.number} of 3</p>
        <h1>{part.name}</h1>
        <p>Choose the one response that most sounds like you. There are no right or wrong answers.</p>
        <div className="form-actions"><button className="button text" onClick={onBack}>Back</button><button className="button" onClick={onContinue}>Continue →</button></div>
      </section>
    </PageShell>
  );
}

function InventoryQuestionScreen({ content, participant, index, busy, error, onBack, onNext }: { content: InventoryContent; participant: Participant; index: number; busy: boolean; error: string; onBack: () => void; onNext: (letter: Letter) => Promise<void> }) {
  const question = content.questions[index];
  const part = content.parts.find((item) => item.number === question.part)!;
  const previous = participant.inventoryAnswers[String(question.number)]?.letter;
  const [selected, setSelected] = useState<Letter | "">(previous || "");
  useEffect(() => setSelected(previous || ""), [previous, question.number]);
  return (
    <PageShell saved>
      <section className="question-card">
        <Progress value={(question.number / 12) * 100} label={`Question ${question.number} of 12`} />
        <p className="part-label">Part {part.number} · {part.name}</p>
        <h1>{question.text}</h1>
        <div className="answer-grid" role="radiogroup" aria-label={question.text}>
          {(Object.entries(question.options) as [Letter, string][]).map(([letter, text]) => (
            <label className={`answer-card ${selected === letter ? "selected" : ""}`} key={letter}>
              <input type="radio" name={`question-${question.number}`} value={letter} checked={selected === letter} onChange={() => setSelected(letter)} />
              <span className="answer-letter">{letter}</span><span>{text}</span><i aria-hidden="true">✓</i>
            </label>
          ))}
        </div>
        <ErrorNotice message={error} />
        <div className="form-actions"><button className="button text" onClick={onBack}>Back</button><button className="button" disabled={!selected || busy} onClick={() => selected && void onNext(selected)}>{busy ? "Saving..." : question.number === 12 ? "See my pathway →" : "Next →"}</button></div>
      </section>
    </PageShell>
  );
}

function PathwayReveal({ content, participant, onContinue }: { content: InventoryContent; participant: Participant; onContinue: () => void }) {
  const pathways = participant.primaryPathways.map((name) => Object.values(content.pathways).find((pathway) => pathway.name === name)!);
  return (
    <PageShell saved className="reveal-shell">
      <section className="reveal-card">
        <p className="eyebrow">Your results are in</p>
        <h1>Your Redemptive {pathways.length > 1 ? "Pathways" : "Pathway"}</h1>
        <div className={`reveal-pathways ${pathways.length > 1 ? "multiple" : ""}`}>
          {pathways.map((pathway) => <div key={pathway.name}><PathwayMark name={pathway.name} large /><h2>{pathway.name}</h2><p>{pathway.description}</p></div>)}
        </div>
        <details className="all-pathways" open>
          <summary><span>All nine Redemptive Pathways</span><span className="details-action" aria-hidden="true" /></summary>
          <div>{Object.values(content.pathways).map((pathway) => <article key={pathway.name}><PathwayMark name={pathway.name} /><div><h3>{pathway.name}</h3><p>{pathway.description}</p></div></article>)}</div>
        </details>
        {pathways.length >= 3 && <p className="tie-note">Your participant record has been flagged for the BRC team because three or more pathways share your highest score.</p>}
        <div className="reveal-bridge"><p><strong>Part 1 is complete, but your full Inventory is not finished yet.</strong> Discovering your pathway is only the beginning.</p><p>Next, the seven-question Builder Journey will help connect this pattern to the people and places already in your life.</p><p>God has uniquely designed you - with gifts, experiences, resources, relationships, and desires that can be used for His redemptive purposes.</p></div>
        <button className="button" onClick={onContinue}>Continue to Part 2: Builder Journey →</button>
      </section>
    </PageShell>
  );
}

function JourneyIntro({ participant, onBack, onContinue }: { participant: Participant; onBack: () => void; onContinue: () => void }) {
  return (
    <PageShell saved>
      <section className="transition-card journey-intro-card">
        <PathwayPill participant={participant} />
        <p className="eyebrow">The Builder Journey</p>
        <h1>A pattern becomes meaningful when it meets your actual life.</h1>
        <p>The next seven questions invite you to reflect on your passions, resources, community, work, giftedness, and calling. Thoughtful, honest responses matter more than lengthy ones.</p>
        <div className="form-actions"><button className="button text" onClick={onBack}>Back</button><button className="button" onClick={onContinue}>Begin the Builder Journey →</button></div>
      </section>
    </PageShell>
  );
}

function PathwayPill({ participant }: { participant: Participant }) {
  return <div className="pathway-pill"><span className="pathway-pill-icons">{participant.primaryPathways.map((pathway) => <PathwayMark name={pathway} key={pathway} />)}</span><span>Your Redemptive {participant.tieCount > 1 ? "Pathways" : "Pathway"}</span><strong>{participant.primaryPathways.join(" + ")}</strong></div>;
}

function JourneyQuestionScreen({ content, participant, index, busy, error, onBack, onNext }: { content: InventoryContent; participant: Participant; index: number; busy: boolean; error: string; onBack: () => void; onNext: (answer: JourneyAnswer) => Promise<void> }) {
  const question = content.journeyQuestions[index];
  const saved = participant.journeyAnswers[String(question.number)];
  const [text, setText] = useState(saved?.text || "");
  const [passions, setPassions] = useState(saved?.passions.length ? saved.passions : ["", "", ""]);
  useEffect(() => { setText(saved?.text || ""); setPassions(saved?.passions.length ? saved.passions : ["", "", ""]); }, [question.number, saved]);
  const valid = question.kind === "passions" ? passions.length === 3 && passions.every((value) => value.trim() && value.length <= 100) : Boolean(text.trim()) && text.length <= 500;
  return (
    <PageShell saved>
      <section className="question-card journey-question-card">
        <PathwayPill participant={participant} />
        <Progress value={(question.number / 7) * 100} label={`Question ${question.number} of 7`} />
        <p className="part-label">The Builder Journey</p>
        <h1>{question.text}</h1>
        {question.kind === "passions" ? (
          <div className="passion-fields">{passions.map((value, passionIndex) => <label key={passionIndex}>Passion {passionIndex + 1}<input maxLength={100} value={value} onChange={(event) => setPassions(passions.map((current, indexValue) => indexValue === passionIndex ? event.target.value : current))} /><span>{value.length} / 100</span></label>)}</div>
        ) : (
          <label className="textarea-field"><span className="sr-only">Your response</span><textarea rows={7} maxLength={500} placeholder="Take a moment to reflect..." value={text} onChange={(event) => setText(event.target.value)} /><span>{text.length} / 500 characters</span></label>
        )}
        <ErrorNotice message={error} />
        <div className="form-actions"><button className="button text" onClick={onBack}>Back</button><button className="button" disabled={!valid || busy} onClick={() => void onNext({ text, passions })}>{busy ? "Saving..." : question.number === 7 ? "Create my summary →" : "Next →"}</button></div>
      </section>
    </PageShell>
  );
}

function CompletionScreen({ participant, onSummary }: { participant: Participant; onSummary: () => void }) {
  return (
    <PageShell>
      <section className="completion-card">
        <img className="completion-city" src={cityArtwork} alt="" />
        <p className="eyebrow">Thank you, {participant.firstName}</p>
        <h1>Discovering your pathway is only the beginning.</h1>
        <p>Thank you for completing the Redemptive Builder Inventory. May your pathway and reflections help you move from inspiration to participation in building redemptive community.</p>
        <div className="completion-links"><button className="button" onClick={onSummary}>View my summary again</button><a className="button secondary" href="https://www.buildingredemptivecommunity.com" target="_blank" rel="noreferrer">Visit Building Redemptive Community</a></div>
        <div className="qr-card"><img src={linktreeQr} alt="QR code to the Building Redemptive Community Linktree" /><div><p className="eyebrow">Stay connected</p><h2>Watch your inbox.</h2><p>You'll receive more information about upcoming opportunities to continue the conversation and connect with other Redemptive Builders.</p></div></div>
      </section>
    </PageShell>
  );
}

function readSession(): { id: string; token: string } | null {
  try {
    const value = JSON.parse(localStorage.getItem(SESSION_KEY) || "null") as { id?: string; token?: string } | null;
    return value?.id && value?.token ? { id: value.id, token: value.token } : null;
  } catch {
    return null;
  }
}

function restorePosition(participant: Participant, setScreen: (screen: Screen) => void, setInventoryIndex: (index: number) => void, setJourneyIndex: (index: number) => void) {
  if (participant.status === "completed") { setScreen("summary"); return; }
  // Old sessions and admin continue links may still point at the retired feedback screens.
  // Restore the saved final answer so summary creation can be retried safely.
  if (participant.stage === "feedback" || participant.stage === "feedback-intro") {
    setJourneyIndex(6);
    setScreen("journey");
    return;
  }
  const stage = participant.stage as Screen;
  setScreen(stage || "part-1");
  if (stage === "inventory") setInventoryIndex(Math.min(participant.currentStep, 11));
  if (stage === "journey") setJourneyIndex(Math.min(participant.currentStep, 6));
}

function sourceFromLocation() {
  const query = new URLSearchParams(window.location.search);
  return query.get("utm_source") || query.get("source") || (document.referrer ? `Referral: ${new URL(document.referrer).hostname}` : "Direct");
}

function messageOf(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}
